import { requestFunnel } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';

const getAuthorizationOptions = () => ({ headers: authenticationService.authHeader() });

const createFinancialPortabilityRequest = async (debtor, financialInstitution,
  includeAllProducts, companyOperationsIds, sender) => {
  const body = { debtor, includeAllProducts, companyOperationsIds, sender };
  body.debtor.idNumber = body.debtor.idNumber.replace(/\./g, '');
  if (financialInstitution) body.financialInstitution = financialInstitution;
  const result = await requestFunnel.post('financial-portability', body);
  return result.data;
};

export const createFinancialPortabilityRequestFromFinancialInstitution = (debtor, financialInstitution,
  includeAllProducts, companyOperationsIds) => {
  return createFinancialPortabilityRequest(
    debtor, financialInstitution, includeAllProducts, companyOperationsIds, 'FINANCIAL_INSTITUTION'
  );
};

export const createFinancialPortabilityRequestFromDebtor = async (
  debtor,
  includeAllProducts,
  companyOperationsIds
) => {
  return createFinancialPortabilityRequest(
    debtor,
    undefined,
    includeAllProducts,
    companyOperationsIds,
    'DEBTOR'
  );
};

export const createFinancialPortabilityRequestFromDebtorPortability = async (
  debtor,
  includeAllProducts,
  companyOperationsIds
) => {
  return createFinancialPortabilityRequest(
    debtor,
    undefined,
    includeAllProducts,
    companyOperationsIds,
    'DEBTOR_PORTABILITY'
  );
};

const addFileToFinancialPortabilityRequest = async (financialPortabilityRequestId, fileName, file, useAuthToken) => {
  const options = useAuthToken ? getAuthorizationOptions() : { headers: {} };
  options.headers['Content-Type'] = 'multipart/form-data';
  options.timeout = 120000;
  const formData = new FormData();
  formData.append(fileName, file);
  const result = await requestFunnel.patch(`financial-portability/${financialPortabilityRequestId}/file`, formData, options);
  return result.data;
};

export const addRequestDocumentToFinancialPortabilityRequest = (financialPortabilityRequestId, file) => {
  return addFileToFinancialPortabilityRequest(financialPortabilityRequestId, 'requestDocument', file);
};

export const addValuationToFinancialPortabilityRequest = (financialPortabilityRequestId, file) => {
  return addFileToFinancialPortabilityRequest(financialPortabilityRequestId, 'valuation', file, true);
};

export const addTitleStudyToFinancialPortabilityRequest = (financialPortabilityRequestId, file) => {
  return addFileToFinancialPortabilityRequest(financialPortabilityRequestId, 'titleStudy', file, true);
};

export const addDebtSettlementToFinancialPortabilityRequest = (financialPortabilityRequestId, file) => {
  return addFileToFinancialPortabilityRequest(financialPortabilityRequestId, 'debtSettlement', file, true);
};

export const addTaxesCertificateToFinancialPortabilityRequest = (financialPortabilityRequestId, file) => {
  return addFileToFinancialPortabilityRequest(financialPortabilityRequestId, 'taxesCertificate', file, true);
};

const getFileFromFinancialPortabilityRequest = async (financialPortabilityRequestId, fileName) => {
  const options = getAuthorizationOptions();
  options.responseType = 'blob';
  const result = await requestFunnel.get(`financial-portability/${financialPortabilityRequestId}/${fileName}`, options);
  return result.data;
};

export const getRequestDocumentFromFinancialPortabilityRequest = financialPortabilityRequestId => {
  return getFileFromFinancialPortabilityRequest(financialPortabilityRequestId, 'request-document');
};
export const getValuationFromFinancialPortabilityRequest = financialPortabilityRequestId => {
  return getFileFromFinancialPortabilityRequest(financialPortabilityRequestId, 'valuation');
};
export const getDebtSettlementFromFinancialPortabilityRequest = financialPortabilityRequestId => {
  return getFileFromFinancialPortabilityRequest(financialPortabilityRequestId, 'debt-settlement');
};
export const getTitleStudyFromFinancialPortabilityRequest = financialPortabilityRequestId => {
  return getFileFromFinancialPortabilityRequest(financialPortabilityRequestId, 'title-study');
};
export const getTaxesCertificateFromFinancialPortabilityRequest = financialPortabilityRequestId => {
  return getFileFromFinancialPortabilityRequest(financialPortabilityRequestId, 'taxes-certificate');
};

export const getPendingFinancialPortabilityRequests = async () => {
  const result = await requestFunnel.get('financial-portability', getAuthorizationOptions());
  return result.data;
};

export const acceptFinancialPortabilityRequest = async financialPortabilityRequestId => {
  const options = getAuthorizationOptions();
  const result = await requestFunnel.patch(`financial-portability/${financialPortabilityRequestId}/accept`, {}, options);
  return result.data;
};

export const rejectFinancialPortabilityRequest = async (financialPortabilityRequestId, comment) => {
  const options = getAuthorizationOptions();
  const result = await requestFunnel.patch(`financial-portability/${financialPortabilityRequestId}/reject`, { comment }, options);
  return result.data;
};

export const getReportFinancialPortabilityRequests = async () => {
  const result = await requestFunnel.get('financial-portability/report', getAuthorizationOptions());
  return result.data;
};
